import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined'
import {
	Box,
	IconButton,
	InputAdornment,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from '@mui/material'
import { DragEvent, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ROUTES } from '@/constants'
import { useAppContext, useAppStore } from '@/hooks'
import { BranchBadge, BranchSection } from '@/pages/User/pages/Branches/components/BranchDetailUi'
import { useOpenTab } from '@/pages/User/pages/Home/components/Dependencies/hooks/useOpenTab'
import { retrieveNodeData } from '@/pages/User/pages/Home/pages/SystemDetail/pages/DeploymentPackages/components/Structure/utils'
import { useAddStructureToReleaseMutation, useDeleteReleaseStructureMutation } from '@/rtkApi'
import {
	ReleaseEnvironmentDto,
	StructureReleaseInfoDto,
} from '@/rtkApi/mddApiSliceGeneratedV2'
import { getApiErrorDescription, logErrorAndNotifyWithToast } from '@/utils/errorHandling'

import { branchSyncLabelKey } from './branchSyncLabel'
import { ConfirmDialog } from './ConfirmDialog'
import { EmptyRow, RowCard } from './rowUi'

type PackageElementsProps = {
	canEdit: boolean
	dpId: number
	elements: StructureReleaseInfoDto[]
	environments: ReleaseEnvironmentDto[]
	onChanged: () => void
}

export const PackageElements = ({
	canEdit,
	dpId,
	elements,
	environments,
	onChanged,
}: PackageElementsProps) => {
	const { t } = useAppContext()
	const navigate = useNavigate()
	const { openTabById } = useOpenTab()
	const nodeState = useAppStore((state) => state.node)
	const [deleteStructure] = useDeleteReleaseStructureMutation()
	const [addStructure] = useAddStructureToReleaseMutation()
	const [isDropActive, setDropActive] = useState(false)
	const [search, setSearch] = useState('')
	const [envCode, setEnvCode] = useState<string | null>(null)
	const [pendingRemove, setPendingRemove] = useState<StructureReleaseInfoDto | null>(null)

	useEffect(() => {
		if (envCode && environments.some((env) => env.code === envCode)) return
		setEnvCode(environments[0]?.code ?? null)
	}, [envCode, environments])

	const visible = useMemo(() => {
		const query = search.trim().toLowerCase()
		if (!query) return elements
		return elements.filter((element) =>
			[element.name, element.code, element.fullPath].some((value) =>
				value?.toLowerCase().includes(query),
			),
		)
	}, [elements, search])

	const addDroppedElement = async (event: DragEvent) => {
		event.preventDefault()
		setDropActive(false)
		const structureId = retrieveNodeData(
			event,
			{ content: elements.map((element) => ({ structureId: element.structureId })) },
			nodeState,
		)
		if (structureId == null) return
		try {
			await addStructure({ body: [structureId], dpId }).unwrap()
			onChanged()
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'PackageElements.addDroppedElement' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'DEPLOYMENT_PACKAGES_UPDATE_FAILED',
				t,
			})
		}
	}

	const openElement = async (structureId?: number) => {
		if (structureId == null) return
		await openTabById(structureId)
		navigate(ROUTES.PROJECT_VIEW)
	}

	const removeElement = async (element: StructureReleaseInfoDto) => {
		setPendingRemove(null)
		if (element.structureId == null) return
		try {
			await deleteStructure({ dpId, structureId: element.structureId }).unwrap()
			onChanged()
		} catch (error) {
			logErrorAndNotifyWithToast({
				context: { operation: 'PackageElements.removeElement' },
				detail: getApiErrorDescription(error),
				error,
				messageKey: 'DEPLOYMENT_PACKAGES_UPDATE_FAILED',
				t,
			})
		}
	}

	return (
		<BranchSection title={`${t('ELEMENTS_IN_RELEASE')} (${elements.length})`}>
			{canEdit ? (
				<Tooltip title={t('RELEASE_DRAG_DROP_HINT')} arrow>
					<Box
						onDragOver={(event) => {
							event.preventDefault()
							setDropActive(true)
						}}
						onDragLeave={() => setDropActive(false)}
						onDrop={(event) => void addDroppedElement(event)}
						data-testid="deployment-package-drop-element"
						sx={{
							alignItems: 'center',
							bgcolor: isDropActive ? 'rgba(31,73,84,0.06)' : '#fff',
							border: '1px dashed',
							borderColor: isDropActive ? '#1F4954' : '#C5CDD0',
							borderRadius: '9px',
							color: '#52666C',
							display: 'flex',
							fontSize: 13,
							fontWeight: 600,
							gap: 1,
							justifyContent: 'center',
							mb: 1.5,
							py: 1.25,
						}}
					>
						<AddOutlinedIcon sx={{ fontSize: 16 }} />
						{t('ADD_ELEMENT')}
					</Box>
				</Tooltip>
			) : null}
			<Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
				<TextField
					size="small"
					value={search}
					placeholder={t('DEPLOYMENT_PACKAGES_SEARCH_ELEMENTS')}
					onChange={(event) => setSearch(event.target.value)}
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<SearchOutlinedIcon sx={{ color: '#A8B4B8', fontSize: 17 }} />
								</InputAdornment>
							),
							sx: { borderRadius: '9px', fontSize: 13, height: 34 },
						},
					}}
					sx={{ flex: '1 1 240px', maxWidth: 360 }}
				/>
				{environments.length ? (
					<ToggleButtonGroup
						exclusive
						size="small"
						value={envCode}
						onChange={(_event, value: string | null) => value && setEnvCode(value)}
						sx={{ '& .MuiToggleButton-root': { fontSize: 11, fontWeight: 700, px: 1.5, py: 0.5 } }}
					>
						{environments.map((env) => (
							<ToggleButton key={env.code} value={env.code ?? ''}>
								{env.code}
							</ToggleButton>
						))}
					</ToggleButtonGroup>
				) : null}
			</Box>

			{visible.length === 0 ? (
				<EmptyRow
					message={
						elements.length
							? t('DEPLOYMENT_PACKAGES_NO_MATCH')
							: t('DEPLOYMENT_PACKAGES_ADD_ELEMENTS_HINT')
					}
				/>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
					{visible.map((element) => (
						<RowCard
							key={element.structureId ?? element.code}
							onClick={() => void openElement(element.structureId)}
							testId={`deployment-package-element-${element.structureId}`}
						>
							<TableChartOutlinedIcon sx={{ color: '#1F4954', fontSize: 16 }} />
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
									<Typography sx={{ color: '#2A2A2A', fontSize: 13, fontWeight: 600 }}>
										{element.name || element.code}
									</Typography>
									{element.type ? (
										<BranchBadge color="#52666C" background="#EEF1F2">
											{element.type}
										</BranchBadge>
									) : null}
									{(() => {
										const syncLabel = branchSyncLabelKey(element.upToDateWithBranch)
										if (!syncLabel) return null
										const upToDate = syncLabel === 'DEPLOYMENT_PACKAGES_UP_TO_DATE'
										return (
											<BranchBadge
												color={upToDate ? '#0F9D58' : '#C26401'}
												background={upToDate ? '#E6F4EA' : '#FFF4E5'}
											>
												{t(syncLabel)}
											</BranchBadge>
										)
									})()}
									{element.deleted ? (
										<BranchBadge color="#C62828" background="#FDECEA">
											{t('DEPLOYMENT_PACKAGES_DELETED')}
										</BranchBadge>
									) : null}
								</Box>
								<Typography
									sx={{
										color: '#9A9A9A',
										fontSize: 11.5,
										mt: 0.25,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
									}}
								>
									{element.fullPath || element.code}
								</Typography>
							</Box>
							<DeploymentVersions element={element} envCode={envCode} />
							<Tooltip title={t('REMOVE')} arrow>
								<span>
									<IconButton
										size="small"
										disabled={!canEdit}
										onClick={(event) => {
											event.stopPropagation()
											setPendingRemove(element)
										}}
										sx={{ '&:hover': { color: '#C62828' } }}
									>
										<DeleteOutlineOutlinedIcon sx={{ fontSize: 17 }} />
									</IconButton>
								</span>
							</Tooltip>
						</RowCard>
					))}
				</Box>
			)}

			<ConfirmDialog
				open={pendingRemove != null}
				danger
				title={t('REMOVE_ELEMENT_FROM_DEPLOYMENT_PACKAGE')}
				message={t('REMOVE_ELEMENT_FROM_DEPLOYMENT_PACKAGE_DESCRIPTION', [
					pendingRemove?.name ?? '',
				])}
				confirmLabel={t('REMOVE')}
				onClose={() => setPendingRemove(null)}
				onConfirm={() => {
					if (pendingRemove) void removeElement(pendingRemove)
				}}
			/>
		</BranchSection>
	)
}

const DeploymentVersions = ({
	element,
	envCode,
}: {
	element: StructureReleaseInfoDto
	envCode: string | null
}) => {
	const { t } = useAppContext()
	const info = element.environmentInfo
		?.find((env) => env.envCode === envCode)
		?.deploymentInfo?.[0]

	if (!envCode) return null

	if (!info) {
		return (
			<BranchBadge color="#9A9A9A" background="#F1F1F1">
				{t('DEPLOYMENT_PACKAGES_NOT_DEPLOYED')}
			</BranchBadge>
		)
	}

	return (
		<Box sx={{ display: 'flex', gap: 0.75 }}>
			{info.deployedVersion != null ? (
				<BranchBadge color="#0F9D58" background="#E6F4EA">
					{t('DEPLOYMENT_PACKAGES_DEPLOYED_VERSION', [String(info.deployedVersion)])}
				</BranchBadge>
			) : null}
			{info.pushedVersion != null ? (
				<BranchBadge color="#2A6FDB" background="#E5EEFB">
					{t('DEPLOYMENT_PACKAGES_PUSHED_VERSION', [String(info.pushedVersion)])}
				</BranchBadge>
			) : null}
		</Box>
	)
}
